'use client'

import Image, { ImageProps } from 'next/image'
import { Ref, useState } from 'react'

import { ALLOWED_ASSETS_DOMAINS } from '@/constants/assets'
import { akamaiImageLoader } from '@/lib/image-loader'

type OptimizedImageProps = ImageProps & {
  ref?: Ref<HTMLImageElement | null>
  overrideSrcWidth?: number
}

export default function OptimizedImage({
  src,
  ref,
  alt,
  onError,
  overrideSrc,
  overrideSrcWidth,
  ...props
}: OptimizedImageProps) {
  const [fallbackSource, setFallbackSource] = useState<string | null>(null)
  const isRemote = typeof src === 'string' && src.startsWith('http')
  const useAkamaiLoader = isRemote && ALLOWED_ASSETS_DOMAINS.some((domain) => src.includes(domain))
  const hasFallenBack = typeof src === 'string' && fallbackSource === src

  const boundedOverrideSrc =
    useAkamaiLoader && !hasFallenBack && typeof src === 'string' && overrideSrcWidth
      ? akamaiImageLoader({ src, width: overrideSrcWidth })
      : undefined

  const resolvedOverrideSrc = hasFallenBack ? undefined : boundedOverrideSrc || overrideSrc

  // ‼️ Currently other domains are not supported to use custom loader
  const unoptimized = isRemote && !ALLOWED_ASSETS_DOMAINS.some((domain) => src.includes(domain))

  const handleError: ImageProps['onError'] = (event) => {
    onError?.(event)

    if (useAkamaiLoader && typeof src === 'string' && !hasFallenBack) {
      setFallbackSource(src)
    }
  }

  return (
    <Image
      ref={ref}
      src={src}
      alt={alt}
      loader={useAkamaiLoader && !hasFallenBack ? akamaiImageLoader : undefined}
      overrideSrc={resolvedOverrideSrc}
      unoptimized={unoptimized || hasFallenBack}
      onError={handleError}
      {...props}
    />
  )
}
